import { ChangeDetectionStrategy, Component, computed, inject, input, signal, viewChild } from '@angular/core';
import { NgClass } from '@angular/common';
import { MatBadge } from '@angular/material/badge';
import { MatIconButton } from '@angular/material/button';
import { MatCard, MatCardContent } from '@angular/material/card';
import { MatIcon } from '@angular/material/icon';
import { MatFormField, MatInput } from '@angular/material/input';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatTooltip } from '@angular/material/tooltip';
import { TranslocoPipe } from '@jsverse/transloco';

import { BaseComponent } from '~components/abstractions/base.component';
import { BaseSearchStore } from '~core/abstractions/base-search.store';
import { Identifyable } from '~core/abstractions/identifyable';
import { AlertService } from '~core/services/alert.service';
import { FilterService } from '~core/services/filter.service';
import { SearchRequestService } from '~core/services/search-request.service';
import { FilterComponent } from './filter/filter.component';

@Component({
    selector: 'arc-search-filter',
    templateUrl: './search-filter.component.html',
    changeDetection: ChangeDetectionStrategy.Default,
    imports: [
        MatCard,
        MatCardContent,
        MatFormField,
        MatInput,
        MatIconButton,
        MatMenuTrigger,
        MatTooltip,
        MatIcon,
        MatBadge,
        NgClass,
        FilterComponent,
        MatMenu,
        MatMenuItem,
        TranslocoPipe
    ]
})
export class SearchFilterComponent<TId = number> extends BaseComponent {
    readonly filter = viewChild('filter', { read: FilterComponent });

    readonly store = input.required<BaseSearchStore<Identifyable<TId>, TId>>();

    readonly searchText = computed(() => this.searchRequestService.currentProperties.searchText() ?? '');
    readonly showFilter = signal(false);
    readonly filterCount = computed(() => this.searchRequestService.currentProperties.filters().length);

    readonly canShowFilter = computed(() => this.selectableFilters().length > 0 || (this.filter()?.readonlyFilters().length ?? 0) > 0);

    // saving / loading filters
    readonly savedFilters = computed(() =>
        this.filterService.savedFilters().map(f => ({ ...f, isDefault: f.name === FilterService.DEFAULT_FILTER_NAME }))
    );
    readonly isDeleting = computed(() => this.filterService.currentState().isDeleting);

    private readonly selectableFilters = computed(() => this.filterService.filterItems().filter(f => !f.isReadOnly));

    private readonly searchRequestService = inject(SearchRequestService);
    private readonly filterService = inject(FilterService);
    private readonly alertService = inject(AlertService);

    searchTextChanged(event: Event): void {
        const value = (event.target as HTMLInputElement).value;
        this.searchRequestService.setSearchRequestPartial({ searchText: value });
    }

    toggleShowFilterAndSearch(): void {
        this.showFilter.set(!this.showFilter());
    }

    applySavedFilter(id: string): void {
        this.filterService.applySavedFilter(id);
    }

    deleteSavedFilter(id: string, event: Event): void {
        event.stopPropagation();

        this.alertService.showConfirm('Components.SearchFilter.DeleteSavedFilterConfirm', undefined, isConfirmed => {
            if (isConfirmed) {
                this.filterService.deleteSavedFilter(id);
            }
        });
    }
}
